import { Alert, Box, Button, Flex, ProgressIndicator, Text } from '@rocket/ui';
import { useState } from 'react';
import { LuFilePlus2, LuFileSearch } from 'react-icons/lu';
import { useFtueHost } from '../host';
import type { ExtractedSection, ExtractionProgress, SlotSurfaceForms } from '../onboarding-api';
import {
  analyzeTemplateDocument,
  extractionCurrentTotal,
  extractionPercentComplete,
  extractionProgressMessage,
} from '../onboarding-api';

interface Props {
  playbookId: string | undefined;
  contractType: string;
  onBack: () => void;
  /** Called when the user elects to skip this step and go to the standard upload */
  onSkip: () => void;
  /** Called when analysis finishes and sections are ready */
  onRulesExtracted: (sections: Array<ExtractedSection>, slots?: SlotSurfaceForms) => void;
  onExtractionStateChange?: (isExtracting: boolean) => void;
}

export function OnboardingFirstPartyAnalysis({
  playbookId,
  contractType,
  onBack,
  onSkip,
  onRulesExtracted,
  onExtractionStateChange,
}: Props) {
  const { authedFetch, getDocument, handleUnauthorized, isAuthError } = useFtueHost();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setError(null);
    setProgress(null);
    onExtractionStateChange?.(true);

    try {
      const file = await getDocument();
      if (!file) {
        setError('Could not read the current document. Make sure the document is saved.');
        return;
      }

      const { sections, slotSurfaceForms } = await analyzeTemplateDocument(
        authedFetch,
        { file, contractType, playbookId },
        (p) => setProgress(p)
      );
      onRulesExtracted(sections, slotSurfaceForms);
    } catch (err) {
      if (isAuthError(err)) {
        handleUnauthorized();
        return;
      }
      const message = err instanceof Error ? err.message : 'Analysis failed. Please try again.';
      setError(message);
    } finally {
      setIsAnalyzing(false);
      setProgress(null);
      onExtractionStateChange?.(false);
    }
  };

  return (
    <Flex.V gap="5" p="6" flex="1">
      <Flex.V gap="1">
        <Text fontWeight="700" fontSize="lg">
          Analyze Your Template
        </Text>
        <Text color="fg.muted" fontSize="sm">
          BlackBoiler can analyze the contract template you currently have open in Word to
          automatically extract your preferred language as first party paper rules.
        </Text>
      </Flex.V>

      <Flex.V gap="3">
        {/* Primary action — analyze the open document */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderColor="border"
          p="4"
          _hover={{ borderColor: 'blue.muted', bg: 'bg.subtle' }}
          cursor={isAnalyzing ? 'default' : 'pointer'}
          onClick={isAnalyzing ? undefined : () => void handleAnalyze()}
        >
          <Flex.H gap="3" align="flex-start">
            <Box color="blue.fg" mt="0.5" flexShrink="0">
              <LuFileSearch size={20} />
            </Box>
            <Flex.V gap="1">
              <Text fontWeight="600" fontSize="sm">
                Analyze open document
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Use the template currently open in Word as your source material. BlackBoiler will
                extract rules from your preferred language automatically.
              </Text>
            </Flex.V>
          </Flex.H>
        </Box>

        {/* Secondary action — upload files manually instead */}
        <Box
          borderWidth="1px"
          borderRadius="lg"
          borderColor="border"
          p="4"
          _hover={{ borderColor: 'border', bg: 'bg.subtle' }}
          cursor="pointer"
          onClick={onSkip}
        >
          <Flex.H gap="3" align="flex-start">
            <Box color="fg.muted" mt="0.5" flexShrink="0">
              <LuFilePlus2 size={20} />
            </Box>
            <Flex.V gap="1">
              <Text fontWeight="600" fontSize="sm" color="fg.muted">
                Upload files instead
              </Text>
              <Text fontSize="xs" color="fg.subtle">
                Upload one or more final executed contracts to extract rules from.
              </Text>
            </Flex.V>
          </Flex.H>
        </Box>
      </Flex.V>

      {/* Progress / status */}
      {(isAnalyzing || error) && (
        <Box>
          {error ? (
            <Alert status="error">{error}</Alert>
          ) : isAnalyzing ? (
            <ProgressIndicator
              message={extractionProgressMessage(progress)}
              current={extractionCurrentTotal(progress).current}
              total={extractionCurrentTotal(progress).total}
              percentComplete={extractionPercentComplete(progress)}
            />
          ) : null}
        </Box>
      )}

      <Flex.H gap="2" justify="flex-end" mt="auto">
        <Button variant="ghost" size="sm" onClick={onBack} disabled={isAnalyzing}>
          Back
        </Button>
        <Button
          colorPalette="gray"
          size="sm"
          loading={isAnalyzing}
          loadingText="Analyzing…"
          onClick={() => void handleAnalyze()}
          disabled={isAnalyzing}
        >
          Analyze Template
        </Button>
      </Flex.H>
    </Flex.V>
  );
}
